import { Property } from 'csstype';
import { SxProps, Theme } from '@mui/material';

type Width = Property.Width;

export interface ColumnLink {
  to: (row: Record<string, any>) => string;
}

export interface ConditionalFormatting {
  condition: (value: any, row?: Record<string, any>) => boolean;
  sx: SxProps<Theme>;
}

interface BaseColumn {
  readonly id: string;
  readonly label: string;
  readonly width?: Width;
  readonly align?: 'left' | 'center' | 'right';
  readonly link?: ColumnLink;
  readonly conditionalFormatting?: ConditionalFormatting;
}

/**
 * 문자열 타입 컬럼
 */
export interface StringColumn extends BaseColumn {
  readonly type: 'string';
}

/**
 * 숫자 타입 컬럼
 */
export interface NumberColumn extends BaseColumn {
  readonly type: 'number';
  readonly format?: 'integer' | 'decimal' | 'currency' | 'percentage';
  readonly decimalPlaces?: number;
  readonly useThousandSeparator?: boolean; // 천 단위 구분자 사용 여부 (기본값: true)
}

/**
 * 날짜 타입 컬럼
 */
export interface DateColumn extends BaseColumn {
  readonly type: 'date';
  readonly format?: string; // dayjs format string (예: 'YYYY. MM. DD.')
}

/**
 * 날짜시간 타입 컬럼
 */
export interface DateTimeColumn extends BaseColumn {
  readonly type: 'datetime';
  readonly format?: string; // dayjs format string (예: 'YYYY. MM. DD. 오전 HH:mm')
}

/**
 * 라벨 타입 컬럼 (색상이 있는 버튼/배지 형태)
 */
export interface LabelColumn extends BaseColumn {
  readonly type: 'label';
  readonly color?: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
  /** 동적 색상 함수: value와 row를 받아서 색상을 반환 */
  readonly getColor?: (
    value: any,
    row?: Record<string, any>
  ) => 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
  readonly variant?: 'filled' | 'outlined'; // MUI Chip은 'filled' 또는 'outlined'만 지원
  /** 동적 variant 함수: value와 row를 받아서 variant를 반환 */
  readonly getVariant?: (value: any, row?: Record<string, any>) => 'filled' | 'outlined';
}

/**
 * 모든 컬럼 타입의 유니온
 */
export type DataTableColumn =
  | StringColumn
  | NumberColumn
  | DateColumn
  | DateTimeColumn
  | LabelColumn;

/**
 * 타입 가드 함수들
 */
export function isStringColumn(col: DataTableColumn): col is StringColumn {
  return col.type === 'string';
}

export function isNumberColumn(col: DataTableColumn): col is NumberColumn {
  return col.type === 'number';
}

export function isDateColumn(col: DataTableColumn): col is DateColumn {
  return col.type === 'date';
}

export function isDateTimeColumn(col: DataTableColumn): col is DateTimeColumn {
  return col.type === 'datetime';
}

export function isLabelColumn(col: DataTableColumn): col is LabelColumn {
  return col.type === 'label';
}
